import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { isProxiedUrl, proxyImageUrl } from '../../../core/utils/image-proxy.util';
import { TooltipDirective } from '../../directives/tooltip.directive';
import { AutoAriaLabelDirective } from '../../directives/aria-label.directive';

declare const FilerobotImageEditor: any;
declare const TABS: any;
declare const TOOLS: any;

export interface ImageEditorResult {
  imageBase64: string;
  mimeType: string;
  width: number;
  height: number;
  name: string;
}

@Component({
  selector: 'app-image-editor',
  standalone: true,
  imports: [CommonModule, TooltipDirective, AutoAriaLabelDirective],
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss']
})
export class ImageEditorComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() imageUrl: string = '';
  @Input() imageName: string = 'image';
  @Input() visible: boolean = false;
  @Output() saved = new EventEmitter<ImageEditorResult>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('editorContainer') editorContainer!: ElementRef<HTMLDivElement>;

  private editorInstance: any = null;
  private editorLoaded = false;
  loading = true;
  enhancing = false;
  aligning = false;
  rotating = false;
  /** Current working source shared by rotate90/autoAlign/autoEnhance: a data URL of the last result, or null meaning the original imageUrl. Reset on every fresh load. */
  private workingSource: string | null = null;
  error = '';

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.loadEditorScript();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] || changes['imageUrl']) {
      if (this.visible && this.imageUrl && this.editorLoaded) {
        setTimeout(() => this.initEditor(), 100);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyEditor();
  }

  private loadEditorScript(): void {
    this.zone.runOutsideAngular(() => {
      if ((window as any).FilerobotImageEditor) {
        this.editorLoaded = true;
        if (this.visible && this.imageUrl) {
          setTimeout(() => this.initEditor(), 100);
        }
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.scaleflex.it/plugins/filerobot-image-editor/4.8.0/filerobot-image-editor.min.js';
      script.onload = () => {
        this.editorLoaded = true;
        if (this.visible && this.imageUrl) {
          setTimeout(() => this.initEditor(), 100);
        }
      };
      script.onerror = () => {
        this.zone.run(() => {
          this.error = 'Failed to load image editor. Please try again.';
          this.loading = false;
        });
      };
      document.head.appendChild(script);
    });
  }

  private initEditor(): void {
    if (!this.editorContainer?.nativeElement || !this.imageUrl) return;
    this.destroyEditor();
    this.workingSource = null;
    this.loading = true;
    this.error = '';

    const FIE = (window as any).FilerobotImageEditor;
    if (!FIE) {
      this.error = 'Image editor not available';
      this.loading = false;
      return;
    }

    this.fetchImageAsBase64(this.imageUrl).then(base64 => {
      this.createEditor(FIE, base64);
    }).catch(() => {
      this.error = 'Unable to load image for editing. Please try again or refresh the page.';
      this.loading = false;
    });
  }

  private fetchImageAsBase64(url: string): Promise<string> {
    const blobToBase64 = (blob: Blob): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject('FileReader failed');
        reader.readAsDataURL(blob);
      });

    // Try direct fetch first — works when CDN allows CORS
    return fetch(url, { mode: 'cors' })
      .then(res => {
        if (!res.ok) throw new Error('Direct fetch failed');
        return res.blob();
      })
      .then(blobToBase64)
      .catch(() => {
        const proxyUrl = proxyImageUrl(environment.apiUrl, url);
        return fetch(proxyUrl)
          .then(res => {
            if (!res.ok) throw new Error('Proxy fetch failed');
            return res.blob();
          })
          .then(blobToBase64);
      });
  }

  private createEditor(FIE: any, imageSource: string): void {
    if (!this.editorContainer?.nativeElement) return;

    const { TABS: EditorTabs, TOOLS: EditorTools } = FIE;

    const config: any = {
      source: imageSource,
      tabsIds: [
        EditorTabs.ADJUST,
        EditorTabs.FINETUNE,
        EditorTabs.FILTERS,
        EditorTabs.WATERMARK,
        EditorTabs.RESIZE
      ],
      defaultTabId: EditorTabs.ADJUST,
      defaultToolId: EditorTools.CROP,
      Adjust: {
        tools: [
          EditorTools.CROP,
          EditorTools.ROTATE,
          EditorTools.FLIP_X,
          EditorTools.FLIP_Y
        ]
      },
      Rotate: {
        angle: 1,
        componentType: 'slider'
      },
      Finetune: {
        tools: ['Brightness', 'Contrast', 'Saturation', 'Warmth', 'Blur', 'Hue']
      },
      Crop: {
        presetsItems: [
          { titleKey: 'Original', ratio: 'original' },
          { titleKey: '1:1', ratio: 1 },
          { titleKey: '4:3', ratio: 4 / 3 },
          { titleKey: '3:2', ratio: 3 / 2 },
          { titleKey: '16:9', ratio: 16 / 9 },
          { titleKey: '2:3', ratio: 2 / 3 },
          { titleKey: '3:4', ratio: 3 / 4 },
          { titleKey: '9:16', ratio: 9 / 16 }
        ]
      },
      Watermark: {
        gallery: [],
        textScalingRatio: 0.33,
        imageScalingRatio: 0.33
      },
      savingPixelRatio: 1,
      previewPixelRatio: 1,
      defaultSavedImageType: 'jpeg',
      defaultSavedImageQuality: 0.92,
      forceToPngInEllipticalCrop: false,
      useBackendTranslations: false,
      language: 'en',
      avoidChangesNotSavedAlertOnLeave: true,
      showBackButton: true,
      theme: {
        palette: {
          'bg-primary-active': '#1e293b',
          'bg-secondary': '#0f172a',
          'accent-primary': '#10b981',
          'accent-primary-active': '#059669',
          'icons-primary': '#e2e8f0',
          'icons-secondary': '#94a3b8',
          'borders-primary': '#334155',
          'borders-secondary': '#1e293b',
          'txt-primary': '#f1f5f9',
          'txt-secondary': '#cbd5e1',
          'txt-primary-invert': '#0f172a'
        },
        typography: {
          fontFamily: 'Inter, system-ui, sans-serif'
        }
      },
      onBeforeSave: () => false,
      onSave: (editedImageObj: any) => {
        // Re-enter Angular zone so the saved event triggers change detection.
        this.zone.run(() => this.handleSave(editedImageObj));
      },
      onClose: () => {
        this.zone.run(() => this.close());
      }
    };

    // Run the editor entirely outside Angular's zone. The filerobot editor is a
    // React widget — its internal update loop (setTimeout, rAF) would otherwise
    // be intercepted by Zone.js and trigger Angular change detection on every
    // React render tick, causing NG0103 infinite loops.
    this.zone.runOutsideAngular(() => {
      try {
        const Editor = FIE.default || FIE;
        this.editorInstance = new Editor(
          this.editorContainer.nativeElement,
          config
        );
        this.editorInstance.render();
        this.zone.run(() => { this.loading = false; });
      } catch (e: any) {
        this.zone.run(() => {
          this.error = 'Failed to initialize editor: ' + (e.message || 'Unknown error');
          this.loading = false;
        });
      }
    });
  }

  private handleSave(editedImageObj: any): void {
    const result: ImageEditorResult = {
      imageBase64: editedImageObj.imageBase64 || '',
      mimeType: editedImageObj.mimeType || 'image/jpeg',
      width: editedImageObj.width || 0,
      height: editedImageObj.height || 0,
      name: editedImageObj.name || this.imageName
    };
    this.saved.emit(result);
    this.close();
  }

  private destroyEditor(): void {
    if (this.editorInstance) {
      try {
        this.editorInstance.terminate();
      } catch (e) {}
      this.editorInstance = null;
    }
  }

  /**
   * Loads `this.imageUrl` into a same-canvas-readable <img>, with a proxy
   * retry on failure — including the failure mode a plain `img.onerror`
   * handler CANNOT see: a cross-origin image that loads fine (onload fires)
   * but taints the canvas, so the first getImageData/toDataURL call throws
   * a SecurityError. That throw happened inside callers' bare `img.onload`
   * with no try/catch, so it propagated uncaught and the busy flag
   * (aligning/enhancing) that was set before the load never got reset —
   * the "Auto Align"/"Auto Enhance" button span forever (2026-07-20, NM
   * client video repro). onErrorReset is always called on final failure so
   * the caller's busy flag is guaranteed to clear.
   */
  private loadImageForCanvas(
    onReady: (img: HTMLImageElement) => void,
    onErrorReset: () => void,
    viaProxy = false,
  ): void {
    // Retrying only helps if the proxy would actually change the URL. When the
    // caller already handed us a proxied source (my-listings does), a "retry
    // via proxy" re-requests the identical URL, so skip straight to the clean
    // give-up instead of spending a second doomed round-trip.
    const canRetryViaProxy = !viaProxy && !isProxiedUrl(this.imageUrl);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        onReady(img);
      } catch (e) {
        // getImageData/toDataURL threw — most commonly a canvas-taint
        // SecurityError from a cross-origin response with no/mismatched
        // CORS headers (presigned S3/CloudFront URLs are the usual
        // culprit). Retry once via the same-origin image-proxy; if that
        // was already the source, or it also fails, give up cleanly.
        if (canRetryViaProxy) {
          this.loadImageForCanvas(onReady, onErrorReset, true);
        } else {
          onErrorReset();
        }
      }
    };
    img.onerror = () => {
      if (canRetryViaProxy) {
        this.loadImageForCanvas(onReady, onErrorReset, true);
      } else {
        onErrorReset();
      }
    };
    // proxyImageUrl is idempotent, so a source the caller already proxied is
    // not wrapped a second time. That double-wrap produced
    // image-proxy?url=<image-proxy url> — the proxy fetching itself.
    img.src = viaProxy ? proxyImageUrl(environment.apiUrl, this.imageUrl) : this.imageUrl;
  }

  /** Loads the current working source (a prior result's data URL, else the original imageUrl) for canvas work. */
  private loadWorkingImage(onReady: (img: HTMLImageElement) => void, onErrorReset: () => void): void {
    if (this.workingSource) {
      // Same-origin data URL from a previous result: no CORS/presign concerns.
      const img = new Image();
      img.onload = () => { try { onReady(img); } catch (e) { onErrorReset(); } };
      img.onerror = () => onErrorReset();
      img.src = this.workingSource;
      return;
    }
    this.loadImageForCanvas(onReady, onErrorReset);
  }

  /** Draws `img` rotated 90 degrees onto a new canvas with width/height swapped. */
  rotateCanvas90(img: { naturalWidth: number; naturalHeight: number } & CanvasImageSource, direction: 'left' | 'right'): HTMLCanvasElement | null {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = h;
    canvas.height = w;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.translate(h / 2, w / 2);
    ctx.rotate((direction === 'right' ? 1 : -1) * Math.PI / 2);
    ctx.drawImage(img, -w / 2, -h / 2);
    return canvas;
  }

  /** Rotate the current image 90 degrees and reload it into the editor (same path as autoAlign). */
  rotate90(direction: 'left' | 'right'): void {
    if (!this.imageUrl || this.rotating || this.loading) return;
    this.rotating = true;

    const onReady = (img: HTMLImageElement) => {
      const canvas = this.rotateCanvas90(img, direction);
      if (!canvas) { this.rotating = false; return; }
      const rotated = canvas.toDataURL('image/jpeg', 0.95);
      this.workingSource = rotated;
      this.destroyEditor();
      this.loading = true;
      const FIE = (window as any).FilerobotImageEditor;
      if (FIE) {
        this.createEditor(FIE, rotated);
      }
      this.rotating = false;
    };

    this.loadWorkingImage(onReady, () => { this.rotating = false; });
  }

  autoAlign(): void {
    if (!this.imageUrl || this.aligning) return;
    this.aligning = true;

    this.loadWorkingImage(
      (img) => {
        const canvas = document.createElement('canvas');
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { this.aligning = false; return; }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, w, h);
        const angle = this.detectSkewAngle(imageData, w, h);

        if (Math.abs(angle) < 0.05) {
          // Already straight
          this.aligning = false;
          return;
        }

        // Rotate by detected angle
        const rad = angle * Math.PI / 180;
        const cos = Math.abs(Math.cos(rad));
        const sin = Math.abs(Math.sin(rad));
        const nw = Math.ceil(w * cos + h * sin);
        const nh = Math.ceil(w * sin + h * cos);
        canvas.width = nw;
        canvas.height = nh;
        ctx.translate(nw / 2, nh / 2);
        ctx.rotate(rad);
        ctx.drawImage(img, -w / 2, -h / 2);

        const alignedBase64 = canvas.toDataURL('image/jpeg', 0.95);
        this.workingSource = alignedBase64;
        this.destroyEditor();
        this.loading = true;
        const FIE = (window as any).FilerobotImageEditor;
        if (FIE) {
          this.createEditor(FIE, alignedBase64);
        }
        this.aligning = false;
      },
      () => { this.aligning = false; },
    );
  }

  private detectSkewAngle(imageData: ImageData, w: number, h: number): number {
    const data = imageData.data;

    // Convert to grayscale edge map using Sobel-like detection
    const gray = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        gray[y * w + x] = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      }
    }

    // Simple edge detection (horizontal gradient)
    const edges = new Float32Array(w * h);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const gx = -gray[(y - 1) * w + (x - 1)] + gray[(y - 1) * w + (x + 1)]
                    - 2 * gray[y * w + (x - 1)] + 2 * gray[y * w + (x + 1)]
                    - gray[(y + 1) * w + (x - 1)] + gray[(y + 1) * w + (x + 1)];
        const gy = -gray[(y - 1) * w + (x - 1)] - 2 * gray[(y - 1) * w + x] - gray[(y - 1) * w + (x + 1)]
                    + gray[(y + 1) * w + (x - 1)] + 2 * gray[(y + 1) * w + x] + gray[(y + 1) * w + (x + 1)];
        edges[y * w + x] = Math.sqrt(gx * gx + gy * gy);
      }
    }

    // Find threshold for strong edges (top 15%)
    const sorted = Array.from(edges).sort((a, b) => b - a);
    const threshold = sorted[Math.floor(sorted.length * 0.15)] || 50;

    // Hough transform for near-horizontal lines (-15 to +15 degrees)
    const angleStep = 0.25;
    const angleRange = 15;
    const numAngles = Math.ceil(angleRange * 2 / angleStep) + 1;
    const accumulator = new Float32Array(numAngles);

    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (edges[y * w + x] < threshold) continue;
        for (let ai = 0; ai < numAngles; ai++) {
          const deg = -angleRange + ai * angleStep;
          const rad = deg * Math.PI / 180;
          accumulator[ai] += edges[y * w + x];
        }
      }
    }

    // More precise: use projection profile method
    // Project edge pixels onto vertical axis at each angle candidate
    let bestAngle = 0;
    let bestScore = 0;
    for (let deg = -10; deg <= 10; deg += 0.25) {
      const rad = deg * Math.PI / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const profile = new Float32Array(h);

      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          if (edges[y * w + x] < threshold) continue;
          const py = Math.round(x * sin + y * cos);
          if (py >= 0 && py < h) profile[py] += edges[y * w + x];
        }
      }

      // Score = variance of profile (straighter = sharper peaks = higher variance)
      let mean = 0;
      for (let i = 0; i < h; i++) mean += profile[i];
      mean /= h;
      let variance = 0;
      for (let i = 0; i < h; i++) variance += (profile[i] - mean) ** 2;
      variance /= h;

      if (variance > bestScore) {
        bestScore = variance;
        bestAngle = deg;
      }
    }

    // Clamp to reasonable correction
    return Math.max(-10, Math.min(10, -bestAngle));
  }

  autoEnhance(): void {
    if (!this.imageUrl || this.enhancing) return;
    this.enhancing = true;

    this.loadWorkingImage(
      (img) => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { this.enhancing = false; return; }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Analyze image: find min/max brightness for auto-levels
        let minB = 255, maxB = 0, totalB = 0;
        for (let i = 0; i < data.length; i += 4) {
          const brightness = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
          if (brightness < minB) minB = brightness;
          if (brightness > maxB) maxB = brightness;
          totalB += brightness;
        }
        const avgB = totalB / (data.length / 4);

        // Auto-levels: stretch histogram
        const range = maxB - minB || 1;
        const contrastBoost = 1.15;
        const saturationBoost = 1.12;
        const brightnessShift = avgB < 100 ? 10 : (avgB > 180 ? -10 : 0);

        for (let i = 0; i < data.length; i += 4) {
          let r = data[i], g = data[i + 1], b = data[i + 2];

          // Auto-levels stretch
          r = ((r - minB) / range) * 255;
          g = ((g - minB) / range) * 255;
          b = ((b - minB) / range) * 255;

          // Brightness
          r += brightnessShift; g += brightnessShift; b += brightnessShift;

          // Contrast
          r = ((r - 128) * contrastBoost) + 128;
          g = ((g - 128) * contrastBoost) + 128;
          b = ((b - 128) * contrastBoost) + 128;

          // Saturation
          const gray = 0.299 * r + 0.587 * g + 0.114 * b;
          r = gray + (r - gray) * saturationBoost;
          g = gray + (g - gray) * saturationBoost;
          b = gray + (b - gray) * saturationBoost;

          data[i] = Math.max(0, Math.min(255, r));
          data[i + 1] = Math.max(0, Math.min(255, g));
          data[i + 2] = Math.max(0, Math.min(255, b));
        }

        ctx.putImageData(imageData, 0, 0);
        const enhancedBase64 = canvas.toDataURL('image/jpeg', 0.95);

        // Reinitialize editor with enhanced image
        this.workingSource = enhancedBase64;
        this.destroyEditor();
        this.loading = true;
        const FIE = (window as any).FilerobotImageEditor;
        if (FIE) {
          this.createEditor(FIE, enhancedBase64);
        }
        this.enhancing = false;
      },
      () => { this.enhancing = false; },
    );
  }

  close(): void {
    this.destroyEditor();
    this.closed.emit();
  }
}
